import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import SortableList from './SortableList';
import DishRow from './DishRow';
import { visibilityNotes } from './labels';

const CategoryHeader = ({ category, dishCount, offShelfCount, showIds, onAddDish, onEditCategory, matches }) => {
  const notes = visibilityNotes(category);
  return (
    <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {category.colorHex && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: category.colorHex }} />}
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            {category.name || 'Untitled'}
          </Typography>
          {category.hideForWeb === true && (
            <Chip size="small" icon={<VisibilityOffOutlinedIcon />} label="Hidden from web" variant="outlined" />
          )}
        </Box>
        {(category.altName || category.description) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[category.altName, category.description].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          <Chip size="small" label={`${dishCount} dish${dishCount === 1 ? '' : 'es'}`} sx={{ bgcolor: 'grey.100' }} />
          {matches != null && <Chip size="small" color="primary" label={`${matches} match${matches === 1 ? '' : 'es'}`} />}
          {offShelfCount > 0 && <Chip size="small" color="error" variant="outlined" label={`${offShelfCount} off shelf`} />}
          {category.cookingPriority ? (
            <Chip size="small" variant="outlined" label={`Cooking priority ${category.cookingPriority}`} />
          ) : null}
          {category.isHiddenForPickup && <Chip size="small" variant="outlined" label="Hidden for pickup" />}
          {notes.map((note) => (
            <Chip key={note} size="small" color="warning" variant="outlined" label={note} />
          ))}
          {showIds && <Chip size="small" variant="outlined" label={`ID ${category.id}`} sx={{ fontFamily: 'monospace' }} />}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="outlined" startIcon={<TuneIcon />} onClick={onEditCategory}>
          Category settings
        </Button>
        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={onAddDish}>
          Add dish
        </Button>
      </Box>
    </Box>
  );
};

CategoryHeader.propTypes = {
  category: PropTypes.object.isRequired,
  dishCount: PropTypes.number.isRequired,
  offShelfCount: PropTypes.number.isRequired,
  showIds: PropTypes.bool,
  onAddDish: PropTypes.func.isRequired,
  onEditCategory: PropTypes.func.isRequired,
  matches: PropTypes.number,
};

/**
 * The dishes of one category, or, while a search or the off-shelf filter is on, every category's
 * matching dishes in sections. Reordering is only offered on the unfiltered list.
 */
const DishesPane = ({ sections, filtering, renderRowProps, onReorderDishes, onAddDish, onEditCategory, showIds, emptyMessage }) => (
  <Paper variant="outlined" sx={{ minHeight: 0, height: '100%', overflowY: 'auto' }} aria-label="Dishes" component="section">
    {sections.length === 0 && (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    )}
    {sections.map(({ category, entries, offShelfCount }) => (
      <Box key={category.id} component="section" aria-label={category.name}>
        <CategoryHeader
          category={category}
          dishCount={(category.items || []).length}
          offShelfCount={offShelfCount}
          showIds={showIds}
          matches={filtering ? entries.length : null}
          onAddDish={() => onAddDish(category.id)}
          onEditCategory={() => onEditCategory(category.id)}
        />
        {!filtering && entries.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No dishes in this category yet.
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onAddDish(category.id)}>
              Add the first dish
            </Button>
          </Box>
        )}
        {filtering ? (
          entries.map(({ item, index }) => <DishRow key={item.id} {...renderRowProps(category, item, index)} />)
        ) : (
          <SortableList
            type={`editor-dish-${category.id}`}
            items={entries}
            getKey={(entry) => entry.item.id}
            onReorder={(from, to) => onReorderDishes(category, from, to)}
            renderItem={(entry, position, drag) => (
              <DishRow {...renderRowProps(category, entry.item, entry.index)} drag={drag} />
            )}
          />
        )}
      </Box>
    ))}
  </Paper>
);

DishesPane.propTypes = {
  sections: PropTypes.array.isRequired,
  filtering: PropTypes.bool,
  renderRowProps: PropTypes.func.isRequired,
  onReorderDishes: PropTypes.func.isRequired,
  onAddDish: PropTypes.func.isRequired,
  onEditCategory: PropTypes.func.isRequired,
  showIds: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

export default DishesPane;
